import { useAtom } from "jotai";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "~/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { StoreRegion, useUpdateSellerMutation } from "~/graphql/graphql.gen";
import { useGetTimezonesQuery } from "~/hooks/use-timezones-query";
import type { Brand } from "~/stores/brand";
import { brandState } from "~/stores/brand";
import { getPhoneCountryCode, parsePhoneNumber } from "~/utils/phone";
import { removeAllSpaces } from "~/utils/string";

import type { BrandContactFormData } from "../brand-info/contact-form-fields";
import { ContactFormFields } from "../brand-info/contact-form-fields";

type SettingsGeneralProps = {
  onPostSave: () => void;
};

type SettingsGeneralFormData = {
  timezone?: string;
} & BrandContactFormData;

export const SettingsGeneral: React.FC<SettingsGeneralProps> = ({
  onPostSave,
}) => {
  const [brand, setBrand] = useAtom(brandState);
  const { i18n, t } = useTranslation();

  const { mutateAsync: updateSellerMutation } = useUpdateSellerMutation();
  const [getTimezones, { data }] = useGetTimezonesQuery();

  const parsedDefaultPhoneNumber = parsePhoneNumber(brand?.contact_phone ?? "");

  const form = useForm<SettingsGeneralFormData>({
    mode: "onTouched",
    defaultValues: {
      email: brand?.contact_email ?? "",
      emailOps: brand?.contact_ops_email ?? "",
      phone:
        parsedDefaultPhoneNumber?.formatInternational().replace(/\s/g, "") ??
        "",
      phoneCountryCode: getPhoneCountryCode(
        parsedDefaultPhoneNumber,
        brand?.store ?? StoreRegion.Fr,
      ),
      timezone:
        brand?.timezone ??
        (brand?.store === StoreRegion.Fr ? "Europe/Paris" : "America/New_York"),
    },
  });

  useEffect(() => {
    getTimezones({ locale: i18n.language === "fr" ? "fr" : "en" });
  }, []);

  const onContactInfoUpdate = (
    data: Pick<
      NonNullable<Brand>,
      "contact_phone" | "contact_email" | "timezone" | "contact_ops_email"
    >,
  ) => {
    if (!brand) return;
    setBrand({
      ...brand,
      contact_email: data.contact_email,
      contact_ops_email: data.contact_ops_email,
      contact_phone: data.contact_phone,
      timezone: data.timezone,
    });
    updateSellerMutation({
      updateSeller: {
        contact_email: data.contact_email,
        contact_ops_email: data.contact_ops_email,
        contact_phone: data.contact_phone,
        timezone: data.timezone,
      },
    });
    onPostSave();
  };

  const onSubmit = (data: SettingsGeneralFormData) => {
    onContactInfoUpdate({
      contact_phone: data.phone,
      contact_email: removeAllSpaces(data.email),
      contact_ops_email: removeAllSpaces(data.emailOps),
      timezone: data.timezone,
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
        <div className="max-w-2.5xl">
          <h3 className="mb-8 text-2xl font-bold">
            {t("settings.general_tabs.contact")}
          </h3>
          <div className="space-y-6">
            <ContactFormFields />
          </div>
        </div>
        <div className="max-w-2.5xl">
          <h3 className="mb-2 text-2xl font-bold">
            {t("settings.general_tabs.timezone")}
          </h3>
          <p className="mb-8 text-sm text-gray-700">
            {t("settings.general_tabs.timezone_details")}
          </p>

          <FormField
            control={form.control}
            name="timezone"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.general_tabs.fields.timezone.label")}
                </FormLabel>
                <Select onValueChange={onChange} value={value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "settings.general_tabs.fields.timezone.empty_field",
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {data?.data.timeZones.map((option) => (
                      <SelectItem
                        value={option.olsonName}
                        key={option.olsonName}
                      >
                        {option.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage match="required">
                  {t(
                    "settings.general_tabs.fields.timezone.validation_errors.required",
                  )}
                </FormMessage>
              </FormItem>
            )}
          />
        </div>
        <Button type="submit">{t("save")}</Button>
      </form>
    </Form>
  );
};

SettingsGeneral.displayName = "SettingsGeneral";
